import type { ICircle, ICoord2D, ILineSegment, IRect } from '@nova/types';

import { closestPointOnSegment } from './line-utils';
import {
  Deg2Rad,
  EPSILON_3,
  EPSILON_4,
  EPSILON_6,
  EPSILON_9,
  EPSILON_12,
  Rad2Deg
} from './math-utils';

export class Point {
  public x: number;
  public y: number;

  public static CreateVectorToPoint(startPos: ICoord2D, targetPoint: ICoord2D): Vector {
    return new Vector(targetPoint.x - startPos.x, targetPoint.y - startPos.y);
  }

  public static CreateVectorToCoords(startPos: ICoord2D, targetPos: ICoord2D): Vector {
    return new Vector(targetPos.x - startPos.x, targetPos.y - startPos.y);
  }

  public constructor(x?: number, y?: number) {
    this.x = x || 0;
    this.y = y || 0;
  }

  public Set(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  public Zero() {
    this.x = 0;
    this.y = 0;
  }

  public VectorToPoint(targetPoint: ICoord2D): Vector {
    return new Vector(targetPoint.x - this.x, targetPoint.y - this.y);
  }

  public VectorToXY(targetX: number, targetY: number): Vector {
    return new Vector(targetX - this.x, targetY - this.y);
  }

  public Add(point: Point) {
    this.x += point.x;
    this.y += point.y;
  }

  public AddValues(x: number, y: number) {
    this.x += x;
    this.y += y;
  }

  public AddCoords(coords: ICoord2D) {
    this.x += coords.x;
    this.y += coords.y;
  }

  public Subtract(point: Point) {
    this.x -= point.x;
    this.y -= point.y;
  }

  public SubtractCoords(coords: ICoord2D) {
    this.x -= coords.x;
    this.y -= coords.y;
  }

  public Equals(p: Point) {
    return this.x === p.x && this.y === p.y;
  }

  public get Coords(): ICoord2D {
    return { x: this.x, y: this.y };
  }

  public Clone(): Point {
    return new Point(this.x, this.y);
  }

  public CloneAdded(vector: Vector): Point {
    return new Point(this.x + vector.x, this.y + vector.y);
  }

  public CloneSubtracted(vector: Vector): Point {
    return new Point(this.x - vector.x, this.y - vector.y);
  }
}

export class Vector extends Point {
  public Normalize() {
    // TODO: Either way better/more efficient?

    // const mag = this.GetMag();
    // if(mag === 0) {
    // }

    // this.x /= mag;
    // this.y /= mag;

    const rad = this.GetRadianAngle();
    this.x = Math.cos(rad);
    this.y = Math.sin(rad);
  }

  public Rotate(degrees: number) {
    const rad = Deg2Rad(degrees);

    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    const newX = (this.x * cos) - (this.y * sin);
    const newY = (this.x * sin) + (this.y * cos);

    this.x = newX;
    this.y = newY;
  }

  public Scale(scalar: number) {
    this.x *= scalar;
    this.y *= scalar;
  }

  public Clone(): Vector {
    return new Vector(this.x, this.y);
  }

  public ClonePoint(): Point {
    return new Point(this.x, this.y);
  }

  public CloneNormalized(): Vector {
    // TODO: Either way better/more efficient?

    // const mag = this.GetMag();
    // if(mag === 0) {
    // }

    // return new Vector(this.x / mag, this.y / mag);

    const rad = this.GetRadianAngle();

    return new Vector(Math.cos(rad), Math.sin(rad));
  }

  public CloneRotated(degrees: number): Vector {
    const rad = Deg2Rad(degrees);

    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    const newX = (this.x * cos) - (this.y * sin);
    const newY = (this.x * sin) + (this.y * cos);

    return new Vector(newX, newY);
  }

  public CloneScaled(scalar: number): Vector {
    return new Vector(this.x * scalar, this.y * scalar);
  }

  public CloneAdded(vector: Vector): Vector {
    return new Vector(this.x + vector.x, this.y + vector.y);
  }

  public CloneSubtracted(vector: Vector): Vector {
    return new Vector(this.x - vector.x, this.y - vector.y);
  }

  public GetSqrMag(): number {
    return (this.x * this.x) + (this.y * this.y);
  }

  public GetMag(): number {
    return Math.sqrt(this.GetSqrMag());
  }

  public GetRadianAngle(): number {
    return Math.atan2(this.y, this.x);
  }

  // Angle from this vector using mathematical Y-up (atan2(y, x)).
  // For engine space (y+south), use directionVectorToAngleDeg from math-utils instead.
  public GetDegreeAngle(): number {
    const rad = this.GetRadianAngle();

    return Rad2Deg(rad);
  }

  public SetRotationRad(radians: number) {
    this.x = Math.cos(radians);
    this.y = Math.sin(radians);
  }

  public SetRotationDeg(degrees: number) {
    this.SetRotationRad(Deg2Rad(degrees));
  }
}

export class GeomUtils {
  /**
   *
   * @param a
   * @param b
   * @returns new ICoords2D interface-object
   */
  public static AddCoords(a: ICoord2D, b: ICoord2D): ICoord2D {
    return { x: a.x + b.x, y: a.y + b.y };
  }

  public static GetVectorAsCoords(start: ICoord2D, target: ICoord2D): ICoord2D {
    return {
      x: target.x - start.x,
      y: target.y - start.y
    };
  }

  public static Rotate(point: ICoord2D, angleRad: number): ICoord2D {
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);

    return {
      x: (point.x * cos) - (point.y * sin),
      y: (point.x * sin) + (point.y * cos)
    };
  }

  // Gets the distance between 2 points, squared (most of the time you dont need the square root if just comparing distances)
  public static GetDistSqr(a: ICoord2D, b: ICoord2D): number {
    const diffVector = {
      x: a.x - b.x,
      y: a.y - b.y
    };
    return this.GetSqrMag(diffVector);
  }

  public static GetSqrMag(coordsAsVector: ICoord2D): number {
    return (coordsAsVector.x * coordsAsVector.x) + (coordsAsVector.y * coordsAsVector.y);
  }

  public static GetMag(coordsAsVector: ICoord2D): number {
    return Math.sqrt(this.GetSqrMag(coordsAsVector));
  }

  public static GetRadianAngle(coordsAsVector: ICoord2D): number {
    return Math.atan2(coordsAsVector.y, coordsAsVector.x);
  }

  /**
   * Will mutate the given coords object.
   * @param coordsAsVector
   */
  public static normalize(coordsAsVector: ICoord2D) {
    const rad = this.GetRadianAngle(coordsAsVector);
    coordsAsVector.x = Math.cos(rad);
    coordsAsVector.y = Math.sin(rad);
  }

  public static getNormalized(coordsAsVector: ICoord2D): ICoord2D {
    const rad = this.GetRadianAngle(coordsAsVector);
    return { x: Math.cos(rad), y: Math.sin(rad) };
  }

  public static cloneScaled(coordsAsVector: ICoord2D, scalar: number): ICoord2D {
    return { x: coordsAsVector.x * scalar, y: coordsAsVector.y * scalar };
  }

  public static cloneCoords(coords: ICoord2D): ICoord2D {
    return { x: coords.x, y: coords.y };
  }

  /**
   * This function will return a scale value required to change an already scaled figure to the next given scale value as if it was currently scaled by 1.
   * E.g. figure x is currently scaled to x3 the original size, and you want it to be x10 the ORIGINAL size (not it's current size),
   * this will return 3.33~, the factor to scale to CURRENT size by to achieve the desired result.
   * The was specifically written because matter.js scales off the current body size, not the original.
   * (After scaling once, you cannot return to the original size by scaling again by x1, it'll do nothing.)
   * @param currenScale The current scale value relative to 1
   * @param nextRelativeScale The next scale value desired to achieve, relative to 1
   * @returns The value to scale the currently scaled figure by.
   */
  public static GetNextAbsoluteScaleFactor(currenScale: number, nextRelativeScale: number) {
    return (1 / currenScale) * nextRelativeScale;
  }

  // This calculates if the queryPoint is anywhere in the 2D plane perfectly perpendicular to the line segment start --> end.
  public static CalcIsInsideLineSegment(startPoint: ICoord2D, endPoint: ICoord2D, queryPoint: ICoord2D) {
    const startToEndX = endPoint.x - startPoint.x;
    const startToEndY = endPoint.y - startPoint.y;

    const L2 = (startToEndX * startToEndX) + (startToEndY * startToEndY);
    if (L2 === 0) { return false; }

    const r = (((queryPoint.x - startPoint.x) * startToEndX) + ((queryPoint.y - startPoint.y) * startToEndY)) / L2;

    return (r >= 0) && (r <= 1);
  }

  /**
   *
   * @param startPoint
   * @param endPoint
   * @param queryPoint
   * @returns true if the queryPoint is within the radius of the line defined by the start and end points.
   */
  public static CalcIsInsideRadius(startPoint: ICoord2D, endPoint: ICoord2D, queryPoint: ICoord2D) {
    const radToEnd = (new Vector(endPoint.x - startPoint.x, endPoint.y - startPoint.y)).GetSqrMag();
    const radToPoint = (new Vector(queryPoint.x - startPoint.x, queryPoint.y - startPoint.y)).GetSqrMag();

    return radToPoint <= radToEnd;
  }

  public static CheckContactCircleWithRect(circle: ICircle, rect: IRect) {
    const halfWidth = rect.width / 2;
    const halfHeight = rect.height / 2;

    const cx = Math.abs(circle.x - rect.x - halfWidth);
    const cy = Math.abs(circle.y - rect.y - halfHeight);
    const xDist = halfWidth + circle.radius;
    const yDist = halfHeight + circle.radius;

    if (cx > xDist || cy > yDist) {
      return false;
    }

    if (cx <= halfWidth || cy <= halfHeight) {
      return true;
    }

    const xCornerDist = cx - halfWidth;
    const yCornerDist = cy - halfHeight;
    const xCornerDistSq = xCornerDist * xCornerDist;
    const yCornerDistSq = yCornerDist * yCornerDist;
    const maxCornerDistSq = circle.radius * circle.radius;

    return (xCornerDistSq + yCornerDistSq <= maxCornerDistSq);
  }

  /**
   * Returns the angle of the line between two points against x-axis
   * @param p1 The first point
   * @param p2 The second point
   * @returns The angle is in radians
   */
  public static GetAngleBetweenPoints(p1: ICoord2D, p2: ICoord2D): number {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;

    return Math.atan2(dy, dx);
  }

  public static GetRandomPointOnCircle(center: ICoord2D, radius: number): ICoord2D {
    const angle = Math.random() * Math.PI * 2;

    const x = center.x + radius * Math.cos(angle);
    const y = center.y + radius * Math.sin(angle);

    return { x, y };
  }

  public static GetRandomPointInCircleArea(center: ICoord2D, radius: number): ICoord2D {
    // if simply use Math.random() to generate a radius value, you would end up with a higher density of points near the center of the circle,
    // rather than a uniform distribution across the entire area.
    const randomRadius = Math.sqrt(Math.random()) * radius;

    return this.GetRandomPointOnCircle(center, randomRadius);
  }

  /**
   * Point-in-polygon (ray casting). Polygon is treated as implicitly closed.
   * Adds EPSILON_12 to the edge dy so near-horizontal edges cannot blow up the crossing x.
   */
  public static PointInPolygon(x: number, y: number, polygon: readonly ICoord2D[]): boolean {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x;
      const yi = polygon[i].y;
      const xj = polygon[j].x;
      const yj = polygon[j].y;
      if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi + EPSILON_12) + xi) { inside = !inside; }
    }
    return inside;
  }

  /**
   * Rejection-samples inside the polygon's axis-aligned bounding box until a point lies inside
   * (`PointInPolygon`), or returns the **average of vertices** (not a geometric centroid).
   *
   * **Distribution:** not uniform over the interior — narrow regions get fewer hits than a uniform sampler would.
   * Acceptable for casual spawn jitter (e.g. filling base-match spawns inside nav); prefer dedicated polygon
   * samplers if uniformity matters.
   */
  public static GetRandomPointInSimplePolygon(polygon: ICoord2D[], maxAttempts = 400): ICoord2D {
    if (polygon.length < 3) { return { x: 0, y: 0 }; }
    let minX = polygon[0].x;
    let maxX = polygon[0].x;
    let minY = polygon[0].y;
    let maxY = polygon[0].y;
    for (const p of polygon) {
      minX = Math.min(minX, p.x);
      maxX = Math.max(maxX, p.x);
      minY = Math.min(minY, p.y);
      maxY = Math.max(maxY, p.y);
    }
    for (let k = 0; k < maxAttempts; k++) {
      const x = minX + Math.random() * (maxX - minX);
      const y = minY + Math.random() * (maxY - minY);
      if (this.PointInPolygon(x, y, polygon)) { return { x, y }; }
    }
    const cx = polygon.reduce((s, p) => s + p.x, 0) / polygon.length;
    const cy = polygon.reduce((s, p) => s + p.y, 0) / polygon.length;
    return { x: cx, y: cy };
  }

  /**
   * World-space corners of a Tiled **rectangle** object: `origin` is the **top-left** of the unrotated rect
   * (same convention as `Matter.Bodies.rectangle` + `Body.setCentre` to that corner, then `Body.setAngle`).
   * Rotation is in **degrees** (Tiled); pivot is `origin`. Winding matches Matter's `Vertices.fromPath` rect: TL → TR → BR → BL.
   */
  public static GetTiledRectangleWorldVertices(origin: ICoord2D, width: number, height: number, rotationDeg: number): ICoord2D[] {
    const { x: px, y: py } = origin;
    const corners: ICoord2D[] = [
      { x: px, y: py },
      { x: px + width, y: py },
      { x: px + width, y: py + height },
      { x: px, y: py + height }
    ];
    const rad = Deg2Rad(rotationDeg);
    if (rad === 0) { return corners; }
    return corners.map((c) => this.AddCoords(origin, this.Rotate(this.GetVectorAsCoords(origin, c), rad)));
  }

  public static CalculateSimpleCenter(vertices: ICoord2D[]): ICoord2D {
    let minX = vertices[0].x;
    let maxX = vertices[0].x;
    let minY = vertices[0].y;
    let maxY = vertices[0].y;

    for (const vertex of vertices) {
      minX = Math.min(minX, vertex.x);
      maxX = Math.max(maxX, vertex.x);
      minY = Math.min(minY, vertex.y);
      maxY = Math.max(maxY, vertex.y);
    }

    return {
      x: (minX + maxX) / 2,
      y: (minY + maxY) / 2
    };
  }

  /**
   * Returns the signed polygon area. Positive values are clockwise when the y-axis points down.
   */
  public static CalculateSignedPolygonArea(vertices: readonly ICoord2D[]): number {
    let area2 = 0;
    for (let i = 0; i < vertices.length; i++) {
      const current = vertices[i];
      const next = vertices[(i + 1) % vertices.length];
      area2 += (current.x * next.y) - (next.x * current.y);
    }
    return area2 / 2;
  }

  /** Returns the area-weighted centroid of a non-degenerate polygon. */
  public static CalculatePolygonCentroid(vertices: readonly ICoord2D[]): ICoord2D {
    let area2 = 0;
    let x = 0;
    let y = 0;
    for (let i = 0; i < vertices.length; i++) {
      const current = vertices[i];
      const next = vertices[(i + 1) % vertices.length];
      const cross = (current.x * next.y) - (next.x * current.y);
      area2 += cross;
      x += (current.x + next.x) * cross;
      y += (current.y + next.y) * cross;
    }

    const factor = 1 / (3 * area2);
    return { x: x * factor, y: y * factor };
  }

  /**
   * Simply returns a vertex on the opposite x-side of another
   * @param baseVert A non-moving vertex uses as the basis on which the other will be relatively moved
   * @param flippingVert The moving vertex
   */
  public static flipVertex(baseVert: ICoord2D, flippingVert: ICoord2D, flipX: boolean, flipY: boolean): ICoord2D {
    const diffX = baseVert.x - flippingVert.x;
    const diffY = baseVert.y - flippingVert.y;
    return {
      x: flipX ? baseVert.x + diffX : flippingVert.x,
      y: flipY ? baseVert.y + diffY : flippingVert.y
    };
  }

  /** Which side of the directed line start->end the point lies on: 1 left, -1 right, 0 on the line. */
  public static getSideOfPoint(point: ICoord2D, lineStart: ICoord2D, lineEnd: ICoord2D, sideEps = EPSILON_3): -1 | 0 | 1 {
    const dx = lineEnd.x - lineStart.x;
    const dy = lineEnd.y - lineStart.y;
    const len = Math.hypot(dx, dy);
    if (len < EPSILON_9) {
      return 0;
    }
    const perp = (dx * (point.y - lineStart.y) - dy * (point.x - lineStart.x)) / len;
    if (perp > sideEps) {
      return 1;
    }

    if (perp < -sideEps) {
      return -1;
    }
    return 0;
  }

  /** Reflect a point across the infinite line through lineStart and lineEnd. */
  public static mirrorPointAcrossLine(point: ICoord2D, lineStart: ICoord2D, lineEnd: ICoord2D, minLenSq = EPSILON_9): ICoord2D {
    const dx = lineEnd.x - lineStart.x;
    const dy = lineEnd.y - lineStart.y;
    const len2 = dx * dx + dy * dy;
    if (len2 < minLenSq) {
      return { x: point.x, y: point.y };
    }
    const u = ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / len2;
    const px = lineStart.x + u * dx;
    const py = lineStart.y + u * dy;
    return { x: 2 * px - point.x, y: 2 * py - point.y };
  }

  /**
   * Parametric position along segment segStart->segEnd where the infinite line lineStart->lineEnd crosses it.
   * Values outside [0, 1] mean the lines meet only on the supporting line, not on the segment.
   *
   * Returns NaN when the line and segment are parallel or near-parallel (the denominator falls within
   * minDenominator of zero). Callers should treat NaN as "no intersection"; this avoids the ±Infinity a
   * raw divide would produce on a zero denominator.
   */
  public static getLineSegmentIntersectionParam(
    lineStart: ICoord2D,
    lineEnd: ICoord2D,
    segStart: ICoord2D,
    segEnd: ICoord2D,
    minDenominator = EPSILON_9
  ): number {
    const llx = lineStart.x - lineEnd.x;
    const lly = lineStart.y - lineEnd.y;
    const den = llx * (segStart.y - segEnd.y) - lly * (segStart.x - segEnd.x);
    if (Math.abs(den) < minDenominator) {
      return NaN;
    }
    const num = (lineStart.x - segStart.x) * lly - (lineStart.y - segStart.y) * llx;
    return num / den;
  }

  /** Intersection of the infinite line through lineStart->lineEnd with the closed segment segStart->segEnd. */
  public static getLineSegmentIntersection(
    lineStart: ICoord2D,
    lineEnd: ICoord2D,
    segStart: ICoord2D,
    segEnd: ICoord2D,
    segmentParamEps = EPSILON_4
  ): ICoord2D | null {
    const t = this.getLineSegmentIntersectionParam(lineStart, lineEnd, segStart, segEnd);
    if (!Number.isFinite(t) || t < -segmentParamEps || t > 1 + segmentParamEps) {
      return null;
    }
    return { x: segStart.x + t * (segEnd.x - segStart.x), y: segStart.y + t * (segEnd.y - segStart.y) };
  }

  /**
   * Twice the signed area of triangle a->b->c. In standard math axes (y pointing up) the value is
   * negative for a counter-clockwise turn and positive for a clockwise one: e.g. (0,0)->(1,0)->(0,1)
   * yields a negative value. The magnitude is twice the triangle area regardless of sign.
   */
  public static getSignedTriangleArea2(a: ICoord2D, b: ICoord2D, c: ICoord2D): number {
    return (c.x - a.x) * (b.y - a.y) - (b.x - a.x) * (c.y - a.y);
  }

  /** True when open segments (p1,p2) and (p3,p4) cross in their interiors (endpoint touches do not count). */
  public static segmentsCrossInterior(p1: ICoord2D, p2: ICoord2D, p3: ICoord2D, p4: ICoord2D): boolean {
    const d1 = this.getSignedTriangleArea2(p3, p4, p1);
    const d2 = this.getSignedTriangleArea2(p3, p4, p2);
    const d3 = this.getSignedTriangleArea2(p1, p2, p3);
    const d4 = this.getSignedTriangleArea2(p1, p2, p4);
    return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0))
      && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
  }

  public static coordsNearlyEqual(a: ICoord2D, b: ICoord2D, eps = EPSILON_3): boolean {
    return Math.abs(a.x - b.x) < eps && Math.abs(a.y - b.y) < eps;
  }

  /**
   * True when at least two direction vectors are not parallel. The test compares the cross product
   * normalized by both vector lengths, so parallelEps is an angular tolerance (the sine of the angle
   * between the vectors) and does not change with vector length. Zero-length vectors are skipped.
   */
  public static directionSetBends(dirs: ICoord2D[], parallelEps = EPSILON_3): boolean {
    if (dirs.length < 2) {
      return false;
    }
    const first = dirs[0];
    const firstLen = Math.hypot(first.x, first.y);
    if (firstLen < EPSILON_9) {
      return false;
    }
    for (let i = 1; i < dirs.length; i++) {
      const di = dirs[i];
      const diLen = Math.hypot(di.x, di.y);
      if (diLen < EPSILON_9) {
        continue;
      }
      const sinAngle = (first.x * di.y - first.y * di.x) / (firstLen * diLen);
      if (Math.abs(sinAngle) > parallelEps) {
        return true;
      }
    }
    return false;
  }
}

/**
 * Closest boundary point on `polygon` to `target`.
 * A polygon with no vertices has no boundary to snap to, so `target` comes back unchanged rather
 * than a point the caller never asked about.
 */
export function ClosestPointOnPolygon(target: ICoord2D, polygon: ICoord2D[]): ICoord2D {
  let closest: ICoord2D = { x: target.x, y: target.y };
  let minDist = Infinity;

  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const point = closestPointOnSegment(a, b, target);
    const distSq = (point.x - target.x) ** 2 + (point.y - target.y) ** 2;

    if (distSq < minDist) {
      minDist = distSq;
      closest = point;
    }
  }

  return closest;
}

/** Closest edge on a polygon's boundary to `target`. */
export interface IClosestPolygonEdge {
  edgeStart: ICoord2D;
  edgeEnd: ICoord2D;
  closestPoint: ICoord2D;
  distance: number;
}

export function getClosestEdgeOnPolygon(target: ICoord2D, polygon: ICoord2D[]): IClosestPolygonEdge | null {
  if (polygon.length < 2) {
    return null;
  }

  let closestHit: IClosestPolygonEdge | null = null;
  for (let i = 0; i < polygon.length; i++) {
    const edgeStart = polygon[i];
    const edgeEnd = polygon[(i + 1) % polygon.length];
    const closestPoint = closestPointOnSegment(edgeStart, edgeEnd, target);
    const distance = Math.hypot(closestPoint.x - target.x, closestPoint.y - target.y);
    if (!closestHit || distance < closestHit.distance) {
      closestHit = { edgeStart, edgeEnd, closestPoint, distance };
    }
  }

  return closestHit;
}

/** Moves `point` toward `origin` by `px`, or returns `origin` when they coincide. */
export function nudgePointToward(origin: ICoord2D, point: ICoord2D, px: number): ICoord2D {
  const dx = origin.x - point.x;
  const dy = origin.y - point.y;
  const len = Math.hypot(dx, dy);
  if (len < EPSILON_6) {
    return { x: origin.x, y: origin.y };
  }
  return {
    x: point.x + (dx / len) * px,
    y: point.y + (dy / len) * px
  };
}

/**
 * Unit normal from `closestPoint` toward free space at `center`.
 * When `centerIsInFreeSpace` is false, the vector is flipped so it still points into free space.
 */
export function freeSpaceNormalFromClosest(center: ICoord2D, closestPoint: ICoord2D, centerIsInFreeSpace: boolean): ICoord2D | null {
  let nx = center.x - closestPoint.x;
  let ny = center.y - closestPoint.y;
  if (!centerIsInFreeSpace) {
    nx = -nx;
    ny = -ny;
  }

  const len = Math.hypot(nx, ny);
  if (len < EPSILON_6) {
    return null;
  }
  return { x: nx / len, y: ny / len };
}

/**
 * Removes the component of `step` that moves against `freeNormal` (into the blocked side).
 * Returns null when the remainder is shorter than `minLength`.
 */
export function removeInwardStepComponent(step: ICoord2D, freeNormal: ICoord2D, minLength: number): ICoord2D | null {
  const alongFree = step.x * freeNormal.x + step.y * freeNormal.y;
  const projected = alongFree >= 0
    ? { x: step.x, y: step.y }
    : {
      x: step.x - alongFree * freeNormal.x,
      y: step.y - alongFree * freeNormal.y
    };

  if (Math.hypot(projected.x, projected.y) < minLength) {
    return null;
  }
  return projected;
}

export function coordDistanceSq(a: ICoord2D, b: ICoord2D): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

/** Whichever of `a` or `b` is closer to `point`. */
export function closerCoord(point: ICoord2D, a: ICoord2D, b: ICoord2D): ICoord2D {
  return coordDistanceSq(point, a) <= coordDistanceSq(point, b) ? a : b;
}

/** True when `hit.closestPoint` lies on an endpoint of `hit`'s edge. */
export function isClosestOnPolygonVertex(hit: IClosestPolygonEdge, tolerancePx: number): boolean {
  const tolSq = tolerancePx * tolerancePx;
  return coordDistanceSq(hit.closestPoint, hit.edgeStart) <= tolSq
    || coordDistanceSq(hit.closestPoint, hit.edgeEnd) <= tolSq;
}

/** Every edge across `polygons` that has an endpoint within `tolerancePx` of `vertex`. */
export function findEdgesSharingVertex(vertex: ICoord2D, polygons: ICoord2D[][], tolerancePx: number): ILineSegment[] {
  const edges: ILineSegment[] = [];
  const tolSq = tolerancePx * tolerancePx;

  for (const polygon of polygons) {
    for (let i = 0; i < polygon.length; i++) {
      const start = polygon[i];
      const end = polygon[(i + 1) % polygon.length];
      if (coordDistanceSq(start, vertex) <= tolSq || coordDistanceSq(end, vertex) <= tolSq) {
        edges.push({ start, end });
      }
    }
  }

  return edges;
}

/**
 * Projects `vector` onto the infinite line of `edgeStart`→`edgeEnd`, scaled to the edge's unit
 * direction. Returns null when the edge is degenerate or the projection length is below `minLength`.
 */
export function projectVectorOntoEdge(vector: ICoord2D, edgeStart: ICoord2D, edgeEnd: ICoord2D, minLength: number): ICoord2D | null {
  const edgeX = edgeEnd.x - edgeStart.x;
  const edgeY = edgeEnd.y - edgeStart.y;
  const edgeLength = Math.hypot(edgeX, edgeY);
  if (edgeLength < EPSILON_6) {
    return null;
  }

  const alongEdge = (vector.x * edgeX + vector.y * edgeY) / edgeLength;
  if (Math.abs(alongEdge) < minLength) {
    return null;
  }

  return {
    x: (edgeX / edgeLength) * alongEdge,
    y: (edgeY / edgeLength) * alongEdge
  };
}

/**
 * Adds clearance along the free-space normal when `center` is closer than `radiusPx` to `contact`.
 * Returns `step` unchanged when already clear enough.
 */
export function separateStepFromContact(
  step: ICoord2D,
  center: ICoord2D,
  contact: IClosestPolygonEdge,
  radiusPx: number,
  centerIsInFreeSpace: boolean
): ICoord2D | null {
  const freeNormal = freeSpaceNormalFromClosest(center, contact.closestPoint, centerIsInFreeSpace);
  if (!freeNormal) {
    return null;
  }

  const deficit = radiusPx - contact.distance;
  if (deficit <= EPSILON_4) {
    return { x: step.x, y: step.y };
  }

  return {
    x: step.x + freeNormal.x * deficit,
    y: step.y + freeNormal.y * deficit
  };
}
